import React, { useCallback, useEffect, useState } from "react";
import {
  FormControl,
  FormLabel,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Select,
  VStack,
} from "@chakra-ui/react";
import {
  getBranches,
  getCostCenters,
  getProjects,
} from "../../../../Api/apiClient";
import { mapOptionName } from "../../../../utils/localizedText";
import { ReportActionButton } from "../../Shared/Components/ReportActionButton";
import { ReportFilterButton } from "../../Shared/Components/ReportFilterButton";
import { reportToolbarStyles } from "../../Shared/Styles/reportToolbarStyles";
import { PERIOD_OPTIONS } from "../../balanceSheetConstants";
import FilterMultiSelect from "./FilterMultiSelect";

export default function MainFilterPopover({
  initialFilters,
  isRTL,
  onApplyFilters,
  texts,
}) {
  const [branches, setBranches] = useState([]);
  const [projects, setProjects] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState(
    initialFilters.branches || [],
  );
  const [selectedProjects, setSelectedProjects] = useState(
    initialFilters.projects || [],
  );
  const [selectedCostCenters, setSelectedCostCenters] = useState(
    initialFilters.costCenters || [],
  );
  const [periodCount, setPeriodCount] = useState(
    initialFilters.period_count || 1,
  );
  const [groupBy, setGroupBy] = useState(initialFilters.group_by || "");
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setSelectedBranches(initialFilters.branches || []);
    setSelectedProjects(initialFilters.projects || []);
    setSelectedCostCenters(initialFilters.costCenters || []);
    setPeriodCount(initialFilters.period_count || 1);
    setGroupBy(initialFilters.group_by || "");
  }, [initialFilters]);

  const loadOptions = useCallback(async () => {
    if (isLoaded) return;

    const [branchesRes, projectsRes, costCentersRes] = await Promise.all([
      getBranches(),
      getProjects(),
      getCostCenters(),
    ]);

    setBranches(
      (branchesRes.data.results || []).map((item) => mapOptionName(item)),
    );
    setProjects(
      (projectsRes.data.results || []).map((item) => mapOptionName(item)),
    );
    setCostCenters(
      (costCentersRes.data.results || []).map((item) => mapOptionName(item)),
    );
    setIsLoaded(true);
  }, [isLoaded]);

  return (
    <Popover placement="bottom-start" isLazy onOpen={loadOptions}>
      <PopoverTrigger>
        <ReportFilterButton>
          {texts.reports?.filter || "Filter"}
        </ReportFilterButton>
      </PopoverTrigger>
      <PopoverContent
        {...reportToolbarStyles.popoverContent}
        w="320px"
        zIndex={1500}
      >
        <PopoverArrow />
        <PopoverHeader fontWeight="bold" p={2}>
          {texts.reports?.filter || "Filter"}
        </PopoverHeader>
        <PopoverCloseButton />
        <PopoverBody p={2}>
          <VStack as="div" spacing={1} align="stretch">
            <FormControl>
              <FormLabel fontSize="xs">
                {texts.reports?.comparePeriod || "Compare Period"}
              </FormLabel>
              <Select
                size="xs"
                value={groupBy}
                onChange={(event) => setGroupBy(event.target.value)}
              >
                <option value="">{texts.reports?.select || "Select"}</option>
                <option value="month">{texts.reports?.month || "Month"}</option>
                <option value="year">{texts.reports?.year || "Year"}</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs">
                {texts.reports?.periods || "Periods"}
              </FormLabel>
              <Select
                size="xs"
                value={periodCount}
                onChange={(event) =>
                  setPeriodCount(parseInt(event.target.value, 10))
                }
              >
                {PERIOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FilterMultiSelect
              label={texts.reports?.branch || "Branch"}
              options={branches}
              selectedValues={selectedBranches}
              onSelectionChange={setSelectedBranches}
              texts={texts}
              isRTL={isRTL}
            />
            <FilterMultiSelect
              label={texts.reports?.project || "Project"}
              options={projects}
              selectedValues={selectedProjects}
              onSelectionChange={setSelectedProjects}
              texts={texts}
              isRTL={isRTL}
            />
            <FilterMultiSelect
              label={texts.reports?.costCenter || "Cost center"}
              options={costCenters}
              selectedValues={selectedCostCenters}
              onSelectionChange={setSelectedCostCenters}
              texts={texts}
              isRTL={isRTL}
            />

            <HStack justify="end" w="full" mt={2}>
              <ReportActionButton
                variant="ghost"
                onClick={() =>
                  onApplyFilters({
                    ...initialFilters,
                    period_count: 1,
                    group_by: "",
                    branches: [],
                    costCenters: [],
                    projects: [],
                  })
                }
              >
                {texts.reports?.clear || "Clear"}
              </ReportActionButton>
              <ReportActionButton
                variant="primary"
                onClick={() =>
                  onApplyFilters({
                    ...initialFilters,
                    period_count: periodCount,
                    group_by: groupBy,
                    branches: selectedBranches,
                    costCenters: selectedCostCenters,
                    projects: selectedProjects,
                  })
                }
              >
                {texts.reports?.apply || "Apply"}
              </ReportActionButton>
            </HStack>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
